/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-one-expression-per-line */
import {
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CoffeeOutlined,
  EyeOutlined,
  LoadingOutlined,
  ProjectOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { Card, Tag, Timeline, Typography } from 'antd'
import React from 'react'
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'

import { format } from 'date-fns'

import { Container, Map, ContainerTimeline } from './styles'

import { useTask } from '~/hooks/Tasks/useTask'
import { LoadingIndicator } from '~/pages/Scale/EditScale/styles'
import { useListContributors } from '~/hooks/Contributors/useListContributors'
import { translate } from '~/utils/locale'

interface Status {
  type: string
  created_at: Date
  initialTime: string
  colors: string
  collaborator_id: string
  location: {
    x: number
    y: number
  }
}

type StatusType =
  | 'received'
  | 'viewed'
  | 'en-route'
  | 'check-in'
  | 'pause-task'
  | 'resume-working'
  | 'checkout'
  | 'canceled'

const TaskStatus: React.FC = () => {
  const { task, isLoading } = useTask()
  const { data } = useListContributors()

  if (isLoading || !task) {
    return (
      <LoadingIndicator>
        <LoadingOutlined style={{ fontSize: 64, margin: 'auto' }} spin />
      </LoadingIndicator>
    )
  }

  const statusMap: Record<
    StatusType,
    {
      color: string
      bg: string
      label: string
      icon: JSX.Element
    }
  > = {
    received: {
      color: '#096dd9',
      bg: '#188fff2f',
      label: translate('TasksTabsStatus.statusReceived'),
      icon: <ProjectOutlined />,
    },
    viewed: {
      color: '#2db7f5',
      bg: '#2db6f51d',
      label: translate('TasksTabsStatus.statusViewed'),
      icon: <EyeOutlined />,
    },
    'en-route': {
      color: '#521e9b',
      bg: '#722ed130',
      label: translate('TasksTabsStatus.statusEnRoute'),
      icon: <CarOutlined />,
    },
    'check-in': {
      color: '#088b55',
      bg: '#088b5421',
      label: translate('TasksTabsStatus.statusCheckIn'),
      icon: <ToolOutlined />,
    },
    'pause-task': {
      color: '#fa8c16',
      bg: '#faad1422',
      label: translate('TasksTabsStatus.statusPauseTask'),
      icon: <CoffeeOutlined />,
    },
    'resume-working': {
      color: '#088b55',
      bg: '#088b5421',
      label: translate('TasksTabsStatus.statusResumeWorking'),
      icon: <ToolOutlined />,
    },
    checkout: {
      color: '#0f4b83',
      bg: '#0f4b8318',
      label: translate('TasksTabsStatus.statusCheckout'),
      icon: <CheckCircleOutlined />,
    },
    canceled: {
      color: '#ce151e',
      bg: '#ce151e28',
      label: translate('TasksTabsStatus.statusCanceled'),
      icon: <CloseCircleOutlined />,
    },
  }

  const statusFormatted = task.status.map((taskStatus: any) => {
    const config = statusMap[taskStatus.type as StatusType]

    const dataFormat = {
      ...taskStatus,
      initialTime: format(
        new Date(taskStatus.created_at),
        `dd/MM/yyyy '${translate('TasksTabsStatus.dateString')}' HH:mm'h'`,
      ),
      colors: config?.color ?? '#133b7c',
    }

    return dataFormat
  })

  const coordinates: any = statusFormatted.filter((coord) => {
    if (coord.location?.x && coord.location?.y !== null) {
      return coord
    }

    return coordinates
  })

  return (
    <Card
      bordered={false}
      title={translate('TasksTabsStatus.cardTitle')}
      style={{
        display: 'inline-block',
        width: '100%',
      }}
    >
      <Container>
        <Map>
          {task ? (
            <MapContainer
              center={
                task.address.location.x && task.address.location.x
                  ? [task.address.location.x, task.address.location.y]
                  : [0, 0]
              }
              zoom={10}
              scrollWheelZoom={false}
              style={{ height: '450px', width: '100%', borderRadius: 10 }}
            >
              <TileLayer
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {task.address.location.x &&
                task.address.location.y &&
                task.address.location.x !== 0 &&
                task.address.location.y !== 0 && (
                  <Marker
                    position={[
                      task.address.location.x,
                      task.address.location.y,
                    ]}
                  />
                )}

              {coordinates.map((c: Status, index: number) => (
                <Marker position={[c.location.x, c.location.y]} key={index}>
                  <Popup>
                    {c.type === 'viewed' ? (
                      <p>{translate('TasksTabsStatus.statusViewed')}</p>
                    ) : (
                      ''
                    )}
                    {c.type === 'en-route' ? (
                      <p>{translate('TasksTabsStatus.statusEnRoute')}</p>
                    ) : (
                      ''
                    )}
                    {c.type === 'check-in' ? (
                      <p>{translate('TasksTabsStatus.statusCheckIn')}</p>
                    ) : (
                      ''
                    )}
                    {c.type === 'pause-task' ? (
                      <p>{translate('TasksTabsStatus.statusPauseTask')}</p>
                    ) : (
                      ''
                    )}
                    {c.type === 'resume-working' ? (
                      <p>{translate('TasksTabsStatus.statusResumeWorking')}</p>
                    ) : (
                      ''
                    )}
                    {c.type === 'checkout' ? (
                      <p>{translate('TasksTabsStatus.statusCheckout')}</p>
                    ) : (
                      ''
                    )}
                    {c.type === 'canceled' ? (
                      <p>{translate('TasksTabsStatus.statusCanceled')}</p>
                    ) : (
                      ''
                    )}
                    <p>{c.initialTime}</p>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          ) : (
            <LoadingOutlined spin />
          )}
        </Map>

        <ContainerTimeline>
          <Timeline>
            {statusFormatted.map((st) => {
              const config = statusMap[st.type as StatusType]

              return (
                <Timeline.Item color={config.color} key={st.id}>
                  {config && (
                    <Tag
                      icon={config.icon}
                      style={{
                        fontSize: 13,
                        color: config.color,
                        backgroundColor: config.bg,
                        borderColor: config.color,
                        marginBottom: 5,
                      }}
                    >
                      {config.label}
                    </Tag>
                  )}

                  {data?.map((c) =>
                    c.id === task.responsible?.id ? (
                      <div key={c.id}>
                        <Typography.Text strong>
                          {c.contact.name}
                        </Typography.Text>
                      </div>
                    ) : null,
                  )}

                  <Typography.Text style={{ color: '#A7A7A7' }}>
                    {st.initialTime}
                  </Typography.Text>
                </Timeline.Item>
              )
            })}
          </Timeline>
        </ContainerTimeline>
      </Container>
    </Card>
  )
}

export default TaskStatus
